import { type FC, useCallback, useMemo, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import Button from 'components/Button'
import UnderlineTabs from 'components/Button/UnderlineTabs'
import { BaseCard } from 'components/Card/style'
import { TabPanel } from 'components/Tab'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivColCentered, FlexDivRowCentered } from 'components/layout/flex'
import { DASHBOARD_SECTION_HEIGHT_PX, MOBILE_FOOTER_HEIGHT } from 'constants/ui'
import ManageIsolatedMarginModal from 'sections/futures/Trade/ManageIsolatedMarginModal'
import AssetsTable from 'sections/futures/UserInfo/AssetsTable'
import ConditionalOrdersTable from 'sections/futures/UserInfo/ConditionalOrdersTable'
import PositionsTable from 'sections/futures/UserInfo/PositionsTable'
import { setOpenModal } from 'state/app/reducer'
import { selectShowModal } from 'state/app/selectors'
import { selectPerpsProvider } from 'state/futures/common/selectors'
import {
	selectActivePositionsCount,
	selectConditionalOrdersCount,
	selectProviderBalanceChanges,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { useLeaderboardData } from 'state/stats/hooks'
import { selectWalletStatsProfile } from 'state/stats/selectors'
import { selectWallet } from 'state/wallet/selectors'
import media from 'styles/media'
import { providerIsCrossMargin } from 'utils/futures'

import Connector from 'containers/Connector'
import { unserializeAccountStat } from 'state/stats/utils'
import AccountBalance from './Overview/AccountBalance'
import MarketWatcher from './Overview/MarketWatcher'
import { OverviewTab } from './Overview/constants'
import PortfolioChart from './PortfolioChart'
import PerpsProviderSwitcher from './shared/PerpsProviderSwitcher'
import TraderProfileHeader from './shared/TraderProfileHeader'

const Overview: FC = () => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const perpsProvider = useAppSelector(selectPerpsProvider)
	const conditionalOrdersCount = useAppSelector(selectConditionalOrdersCount)
	const positionsCount = useAppSelector(selectActivePositionsCount)
	const wallet = useAppSelector(selectWallet)
	const { balanceChange, balanceChangePercent } = useAppSelector(selectProviderBalanceChanges)
	const [activeTab, setActiveTab] = useState<OverviewTab>(OverviewTab.POSITIONS)
	const [maskAsSensitive, toggleMaskAsSensitive] = useReducer((o) => !o, false)
	const openModal = useAppSelector(selectShowModal)
	const walletStatsProfile = useAppSelector(selectWalletStatsProfile)
	const { login: handleLogin } = Connector.useContainer()

	useLeaderboardData()

	const userTabs = useMemo(
		() => [
			{
				name: OverviewTab.POSITIONS,
				label: t('dashboard.overview.tabs.positions'),
				badge: positionsCount,
				active: activeTab === OverviewTab.POSITIONS,
				onClick: (): void => {
					setActiveTab(OverviewTab.POSITIONS)
				},
				component: <PositionsTable primaryBackground />,
			},
			{
				name: OverviewTab.ORDERS,
				label: t('dashboard.overview.tabs.orders'),
				active: activeTab === OverviewTab.ORDERS,
				badge: conditionalOrdersCount,
				onClick: (): void => {
					setActiveTab(OverviewTab.ORDERS)
				},
				component: <ConditionalOrdersTable primaryBackground />,
			},
			{
				name: OverviewTab.BALANCES,
				label: t('dashboard.overview.tabs.balances'),
				active: activeTab === OverviewTab.BALANCES,
				onClick: (): void => {
					setActiveTab(OverviewTab.BALANCES)
				},
				component: <AssetsTable primaryBackground />,
				hide: !providerIsCrossMargin(perpsProvider),
			},
		],
		[perpsProvider, activeTab, conditionalOrdersCount, positionsCount, t]
	)

	const onDismiss = useCallback(() => {
		dispatch(setOpenModal(null))
	}, [dispatch])

	return (
		<OverviewContainer rowGap="25px">
			<OverviewHeader>
				<TraderProfileHeader {...unserializeAccountStat(walletStatsProfile)} />
				<PerpsProviderSwitcher />
			</OverviewHeader>
			<CardContainer>
				<AccountBalance
					maskAsSensitive={maskAsSensitive}
					toggleMaskAsSensitive={toggleMaskAsSensitive}
					balanceChange={balanceChange}
					balanceChangePercent={balanceChangePercent}
				/>
				<ChartContainer $noPadding>
					{wallet ? (
						<PortfolioChart />
					) : (
						<FlexDivColCentered rowGap="15px">
							<Body color="secondary">{t('dashboard.overview.no-wallet-connected')}</Body>
							<Button variant="solid-highlight" size="small" onClick={handleLogin}>
								{t('futures.market.trade.trade-balance.connect-wallet-button')}
							</Button>
						</FlexDivColCentered>
					)}
				</ChartContainer>
				<MarketWatcher />
			</CardContainer>
			<FlexDivCol rowGap="20px">
				<TabButtonsContainer>
					<UnderlineTabs options={userTabs.filter((t) => !t.hide)} />
				</TabButtonsContainer>
				<TableContainer>
					{userTabs
						.filter((t) => !t.hide)
						.map(({ name, component }) => (
							<CollapsibleTabPanel
								key={name}
								collapsed={true}
								name={name}
								activeTab={activeTab}
								fullHeight
							>
								{component}
							</CollapsibleTabPanel>
						))}
				</TableContainer>
			</FlexDivCol>
			{openModal === 'futures_deposit_withdraw_isolated_margin' && (
				<ManageIsolatedMarginModal onDismiss={onDismiss} />
			)}
		</OverviewContainer>
	)
}

const OverviewHeader = styled(FlexDivRowCentered)`
	${media.lessThan('sm')`
		flex-direction: column;
		align-items: flex-start;
		row-gap: 15px;
	`}
`
const ChartContainer = styled(BaseCard)`
	grid-column: span 1;
	flex: 8;
	border-radius: 8px;
	min-width: 340px;
	height: ${DASHBOARD_SECTION_HEIGHT_PX};
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;

	${media.lessThan('xl')`
		grid-column: span 2;
		grid-row-start: 1;
	`}
`

const CardContainer = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	grid-template-rows: auto;
	grid-column-gap: 15px;
	row-gap: 25px;

	${media.lessThan('xl')`
		grid-template-columns: 1fr 1fr;
		`}

	${media.lessThan('md')`
		grid-template-columns: 1fr 1fr;
	`}
`

const OverviewContainer = styled(FlexDivCol)`
	margin-top: 25px;
	margin-bottom: 25px;
	height: 100%;
	${media.lessThan('lg')`
		margin: 15px 15px 0 15px;
	`}
`

const CollapsibleTabPanel = styled(TabPanel).attrs({ fade: true })<{ collapsed: boolean }>`
	display: flex;
	flex-direction: column;
	flex: ${({ collapsed }) => (collapsed ? '0' : 'auto')};
	transition: flex 0.4s ease-in-out;
`

const TableContainer = styled.div`
	border: ${(props) => props.theme.colors.selectedTheme.border};
	border-top: none;
	border-radius: 8px;
	overflow: hidden;

	${media.lessThan('lg')`
		border-top: ${(props) => props.theme.colors.selectedTheme.border};
		margin-bottom: calc(${MOBILE_FOOTER_HEIGHT} + 20px);
	`}
`

const TabButtonsContainer = styled.div`
	display: flex;
	min-height: 46px;
	gap: 15px;
	width: 100%;

	button {
		font-size: 13px;
	}

	> div > div {
		border-right: none;
		padding-left: 0px;
	}

	border-bottom: ${(props) => props.theme.colors.selectedTheme.border};
`

export default Overview
